"use client";
import { Button } from "@/components/ui/button";
import { IAccountAssets, IBlocks, IUserData } from "@/types";
import React from "react";
import { BlockUpdateFunction } from ".";
import AdminPopover from "../../content/AdminPopover";
import { SettingTabs } from "../../content/SettingTabs";
import ImageOptions, { BlockOptions } from "./tabs/image/options";
import BlockMetadata from "./tabs/metadata";
import { Separator } from "@/components/ui/separator";

interface Props {
    admin?: boolean;
    user: IUserData;
    block: IBlocks;
    update: BlockUpdateFunction;
    assets: IAccountAssets[];
    newBlock?: string | null;
    impersonate?: IUserData | null;
}
export default function BlockImage({
    admin,
    user,
    block,
    update,
    assets,
    newBlock,
    impersonate,
}: Props) {
    const [isExpanded, setIsExpanded] = React.useState(false);
    React.useEffect(() => {
        if (newBlock === block.id) {
            handleExpand();
        }
        return () => {
            window.removeEventListener("closeBlock", handleClose);
        };
    }, [newBlock, block.id]);
    React.useEffect(() => {
        window.addEventListener("closeBlock", {
            handleEvent: (event: CustomEvent) => {
                if (event.detail.id != block.id) {
                    if (isExpanded) {
                        setIsExpanded(false);
                    }
                }
            },
        });
    });
    React.useEffect(() => {
        if (block != blockUpdate) {
            setBlockUpdate(block);
        }
    }, [block]);
    const handleClose = () => {
        setIsExpanded(false);
        window.dispatchEvent(new CustomEvent("collapseBlock"));
    };
    const [blockUpdate, setBlockUpdate] = React.useState(block);
    function handleUpdate(value: IBlocks) {
        if (value == null) {
            update(blockUpdate.id, value, true);
            return;
        }
        setBlockUpdate({
            ...blockUpdate,
            ...value,
        });
        if (block == value) return;
        update(blockUpdate.id, value);
    }
    const event = new CustomEvent("expandBlock", {
        detail: {
            id: blockUpdate.id,
            title: "Image",
            titleOptions: (
                <BlockOptions
                    block={blockUpdate}
                    blockUpdate={setBlockUpdate}
                    update={handleUpdate}
                    assets={assets}
                    account={user}
                />
            ),
            content: (
                <div className="flex flex-col gap-3 pb-3">
                    <ImageOptions
                        block={blockUpdate}
                        blockUpdate={setBlockUpdate}
                        update={handleUpdate}
                        assets={assets}
                        account_id={user.id}
                        impersonate={
                            impersonate
                                ? {
                                      id: impersonate.id,
                                  }
                                : null
                        }
                        account={user}
                    />
                    <BlockMetadata block={blockUpdate} update={handleUpdate} />
                </div>
            ),
        },
    });
    const handleExpand = () => {
        window.dispatchEvent(
            new CustomEvent("closeBlock", {
                detail: {
                    id: block.id,
                },
            })
        );
        setIsExpanded(true);
        window.dispatchEvent(event);
    };
    React.useEffect(() => {
        if (isExpanded) {
            window.dispatchEvent(event);
        }
    }, [blockUpdate]);
    let size = "w-full";
    if (blockUpdate.options?.size == 20) {
        size = "w-1/6";
    }
    if (blockUpdate.options?.size == 40) {
        size = "w-2/6";
    }
    if (blockUpdate.options?.size == 60) {
        size = "w-4/6";
    }
    if (blockUpdate.options?.size == 80) {
        size = "w-5/6";
    }
    if (blockUpdate.options?.size == 100) {
        size = "w-full";
    }
    const image_id =
        typeof blockUpdate.url == "string"
            ? blockUpdate.url.split("/").pop()?.split(".")[0]
            : null;
    const image_name = assets.find(
        (asset) => asset.url.split("/").pop()?.split(".")[0] == image_id
    )?.title;
    if (blockUpdate.options?.link?.length != 0) {
        return (
            <AdminPopover
                admin={admin}
                className={block.show ? "" : "opacity-30"}
                title={`Image - ${image_name}`}
                onClick={handleExpand}
                isOpen={isExpanded}
                onClose={handleClose}
            >
                <a
                    target="_blank"
                    href={blockUpdate.options?.link}
                    className="w-full"
                >
                    <img
                        id="uploaded-image"
                        src={blockUpdate.url}
                        className={`mx-auto rounded-xl ${size}`}
                        alt={blockUpdate.name}
                    />
                </a>
            </AdminPopover>
        );
    }
    return (
        <AdminPopover
            admin={admin}
            className={block.show ? "" : "opacity-30"}
            title={`Image - ${image_name}`}
            onClick={handleExpand}
            isOpen={isExpanded}
            onClose={handleClose}
        >
            <img
                id="uploaded-image"
                src={blockUpdate.url}
                className={`mx-auto rounded-xl  ${size}`}
                alt={blockUpdate.name}
            />
        </AdminPopover>
    );
}
